// ============================================================
// InjuryDetailsSection.tsx
// Stage 2 redesign (approved mockup casevo-case-sections-batch1.html §3):
// the shared SectionCards view/edit pattern — Injuries · Severity ·
// Description of injuries — with the at-a-glance strip and shared footer.
// DATA LOGIC IS UNCHANGED from the old form:
//   • every area tile is a real checkbox → `update` (live-store spread →
//     updateCase → debounced autosave), fieldKey injuries.injuries
//   • severity → update + broadcastFieldEditNow (instant live reveal)
//   • severe details = the same Yjs RichTextEditor (doc
//     case:<id>:exceptionallySevereDetails), mounted only while severe
//   • description = the same Yjs RichTextEditor (doc
//     case:<id>:descriptionOfInjuries) whose snapshot writes
//     clientDetails.descriptionOfInjuries via the LIVE clientDetails spread
//   • validateInjuryDetails gates Mark complete (SectionFooter)
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { forOpenCase } from '../../utils/caseSnapshotGuard'
import { InjuryDetails } from '../../types/case.types'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import { inpCls } from '../clientInfo/clientInfoRegistry'
import RichTextEditor from '../shared/RichTextEditor'
import { useCollabUser } from '../../hooks/useCollabUser'
import { broadcastFieldEdits, broadcastFieldEditNow } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import DOMPurify from 'dompurify'
import { validateInjuryDetails } from '../../utils/validation'
import { useShouldShowOnboardingPrompt } from '../../hooks/useOnboardingPrompt'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import {
  KIcon, Glance, GlanceDash, SectionFooter, Kv, Empty, Muted, CondLabel, SegRadios, Tile, LiveDocEdit, Clamp, LiveDocView,
  DL1, CHIP, CHIP_N, REVEALED, HINT, htmlHasText, wordCount,
} from '../case/sections/sectionKit'

const INJURY_FIELDS: { key: keyof InjuryDetails; label: string }[] = [
  { key: 'neck', label: 'Neck' },
  { key: 'shoulder', label: 'Shoulder' },
  { key: 'back', label: 'Back' },
  { key: 'ribsChestTorso', label: 'Ribs/Chest/Torso' },
  { key: 'faceCheckboneJawNose', label: 'Face/Cheekbone/Jaw/Nose' },
  { key: 'forearmWristHandFingers', label: 'Forearm/Wrist/Hand/Fingers' },
  { key: 'damageToTeeth', label: 'Damage to Teeth' },
  { key: 'headSenses', label: 'Head/Senses' },
  { key: 'hipsPelvisGenitals', label: 'Hips/Pelvis/Genitals' },
  { key: 'legKneeAnkleFootToe', label: 'Leg/Knee/Ankle/Foot/Toe' },
  { key: 'psychological', label: 'Psychological' },
  // B3 (B2c item 4) — disease, clinical negligence and fatal claims.
  { key: 'hearingEars', label: 'Hearing / ears' },
  { key: 'lungsBreathing', label: 'Lungs / breathing' },
  { key: 'skin', label: 'Skin' },
  { key: 'internalOrgans', label: 'Internal organs' },
  { key: 'fatal', label: 'Fatal injuries (death)' },
  { key: 'other', label: 'Other (describe)' },
]
// True when a rich-text value has no visible text (empty, whitespace, or just
// empty tags like `<p></p>`).
const htmlIsEmpty = (s: string | null | undefined): boolean =>
  !s || s.replace(/<[^>]*>/g, '').trim() === ''
// Field → existing fieldKey (Cancel-restore broadcast). The 11 areas share the
// composite `injuries.injuries` key (caseFieldRegistry injuryGrid).
const FIELD_KEY = (f: keyof InjuryDetails): string | undefined =>
  f === 'exceptionallySevere' ? 'injuries.exceptionally-severe'
    : INJURY_FIELDS.some((x) => x.key === f) ? 'injuries.injuries' : undefined

// Area chip (mockup .area, :315-317).
const AREA = 'inline-flex items-center gap-[7px] text-[13.5px] font-semibold px-[11px] py-[6px] rounded-[9px] bg-casevo-mint/70 text-casevo-forest border border-casevo-brand/25'

const InjuryDetailsSection: React.FC = () => {
  const { currentCase, updateCase } = useCasevoStore()
  // Collab identity for the Yjs carets others see. Hooks must run before the
  // early return below.
  const collabUser = useCollabUser()
  // "Empty" = no injury checkbox is set and the exceptionally-severe
  // free-text is empty.
  const injuries = currentCase?.injuryDetails
  const isEmpty = !!injuries
    && INJURY_FIELDS.every(({ key }) => !injuries[key])
    && !injuries.exceptionallySevere
    && htmlIsEmpty(injuries.exceptionallySevereDetails)
  const showPrompt = useShouldShowOnboardingPrompt(isEmpty)
  if (!currentCase || !injuries) return null
  const errors = validateInjuryDetails(currentCase)
  // Spread the LIVE store object (see AccidentDetailsSection.update) — the
  // rich-text snapshot callback is a stale closure.
  const update = (field: keyof InjuryDetails, value: unknown) => {
    const live = useCasevoStore.getState().currentCase?.injuryDetails ?? injuries
    updateCase({ injuryDetails: { ...live, [field]: value } })
  }
  const updateMany = (changes: Partial<InjuryDetails>) => {
    const live = useCasevoStore.getState().currentCase?.injuryDetails ?? injuries
    updateCase({ injuryDetails: { ...live, ...changes } })
  }
  const inj = injuries
  const severeDoc = `case:${currentCase.id}:exceptionallySevereDetails`
  const descDoc = `case:${currentCase.id}:descriptionOfInjuries`
  const descHtml = currentCase.clientDetails.descriptionOfInjuries ?? ''
  const severeSnapshot = forOpenCase(currentCase.id, (html: string) => update('exceptionallySevereDetails', html))
  const descSnapshot = forOpenCase(currentCase.id, (html: string) => {
    // Live store copy — this callback is a stale closure (fires ~1.5s after
    // typing), so spreading currentCase.clientDetails reverted client-detail
    // edits made meanwhile.
    const live = useCasevoStore.getState().currentCase?.clientDetails ?? currentCase.clientDetails
    updateCase({ clientDetails: { ...live, descriptionOfInjuries: html } })
  })
  const token = (): string => localStorage.getItem('casevo_token') ?? ''
  const affected = INJURY_FIELDS.filter(({ key }) => inj[key])
  const unaffected = INJURY_FIELDS.filter(({ key }) => !inj[key])

  const defs: CardDef<InjuryDetails>[] = [
    {
      key: 'injuries', title: 'Injuries', sub: 'Areas affected · at least one is required', icon: <KIcon name="heart" size={17} />, full: true,
      fields: [...INJURY_FIELDS.map((f) => f.key), 'otherDetails'], errorKeys: ['injuries', 'injuryOtherDetails'],
      view: (ctx) => affected.length === 0 ? <Empty onAdd={ctx.onAdd} /> : (
        <>
          <div className="flex flex-wrap gap-2">
            {affected.map(({ key, label }) => (
              <span key={key} className={AREA}>
                <span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex"><KIcon name={key === 'psychological' ? 'brain' : 'heart'} size={13} /></span>{key === 'other' && inj.otherDetails.trim() ? `Other: ${inj.otherDetails.trim()}` : label}
              </span>
            ))}
          </div>
          {unaffected.length > 0 && (
            <div className="mt-3 text-[12.5px] text-casevo-text-soft leading-[1.6]">
              <b className="font-semibold">Not affected ({unaffected.length}):</b> {unaffected.map((u) => u.label).join(' · ')}
            </div>
          )}
        </>
      ),
      edit: (ctx) => {
        const err = ctx.err('injuries')
        return (
          <FormField variant="info" label="Areas affected" fieldKey="injuries.injuries" required>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {INJURY_FIELDS.map(({ key, label }) => (
                <Tile key={key} label={label} checked={inj[key] as boolean} onChange={(checked) => update(key, checked)} />
              ))}
            </div>
            <FieldError message={err} />
            {/* B3 (B2c item 4) — "Other (describe)" needs a description. */}
            {inj.other && (
              <div className={`mt-3 ${REVEALED}`}>
                <label htmlFor="injury-other-details" className="block text-[12.5px] font-semibold text-casevo-forest mb-1">Describe the other injury <span className="text-casevo-danger">*</span></label>
                <input id="injury-other-details" type="text" maxLength={300} value={inj.otherDetails}
                  aria-invalid={ctx.err('injuryOtherDetails') ? true : undefined}
                  onChange={(e) => update('otherDetails', e.target.value)}
                  placeholder="e.g. Burns to the left forearm"
                  className={inpCls(!!ctx.err('injuryOtherDetails'))} />
                <FieldError message={ctx.err('injuryOtherDetails')} />
              </div>
            )}
          </FormField>
        )
      },
    },
    {
      key: 'severity', title: 'Severity', sub: 'Flags the claim as exceptionally severe', icon: <KIcon name="alert" size={17} />,
      fields: ['exceptionallySevere'],
      presenceKeys: ['injuries.exceptionally-severe', 'injuries.exceptionally-severe-details'],
      hint: 'Severity saves instantly · details are a live document',
      view: (ctx) => (
        <dl className={DL1}>
          <Kv label="Exceptionally severe">
            {inj.exceptionallySevere
              ? <span className={CHIP}><KIcon name="alert" size={12} />Yes — exceptionally severe</span>
              : <span className={CHIP_N}>No</span>}
          </Kv>
          <Kv label="Exceptionally severe details" cond="if Yes">
            {!inj.exceptionallySevere ? <Muted>Only asked when severity is Yes</Muted>
              : (
                // LIVE read-only view (saved copy until synced / if collab is down).
                <div className="min-w-0 w-full">
                  <Clamp moreLabel="Open to edit" onMore={ctx.onAdd} showMore={htmlHasText(inj.exceptionallySevereDetails)}>
                    <LiveDocView docName={severeDoc} html={inj.exceptionallySevereDetails} onSnapshot={severeSnapshot} collabUser={collabUser} empty={<Empty onAdd={ctx.onAdd} />} />
                  </Clamp>
                </div>
              )}
          </Kv>
        </dl>
      ),
      edit: (ctx) => (
        <div className="grid grid-cols-1 gap-y-3">
          <FormField variant="info" label="Exceptionally severe" fieldKey="injuries.exceptionally-severe">
            <SegRadios
              name={`${ctx.idPrefix}-severe`}
              ariaLabel="Exceptionally severe"
              options={[true, false].map((val) => ({
                label: val ? 'Yes — exceptionally severe' : 'No',
                checked: inj.exceptionallySevere === val,
                onSelect: () => {
                  // update() is synchronous (Zustand), so the store already holds
                  // the new value; broadcastFieldEditNow sends the reveal state to
                  // co-viewers IMMEDIATELY. The general change listener still
                  // fires the authoritative persist + base-advance.
                  update('exceptionallySevere', val)
                  broadcastFieldEditNow('injuries.exceptionally-severe')
                },
              }))}
            />
            <p className={HINT}>Ticking this reveals a details box for everyone viewing the case.</p>
          </FormField>
          {/* Exceptionally-severe details — Yjs collaborative rich-text, gated on
              the SHARED, persisted flag (every editing viewer co-mounts it). */}
          {inj.exceptionallySevere && (
            <div className={REVEALED}>
              <FormField variant="info" label={<CondLabel label="Exceptionally severe details" cond="shown when Yes" />} fieldKey="injuries.exceptionally-severe-details">
                <LiveDocEdit>
                  {(onStatus) => (
                    <RichTextEditor
                      collabDocName={severeDoc}
                      collabToken={token}
                      initialHtml={DOMPurify.sanitize(inj.exceptionallySevereDetails ?? '')}
                      onSnapshot={severeSnapshot}
                      placeholder="Please describe the exceptionally severe injuries…"
                      collabUser={collabUser}
                      onCollabStatus={onStatus}
                    />
                  )}
                </LiveDocEdit>
              </FormField>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'description', title: 'Description of injuries', sub: 'The client’s account, in their words', icon: <KIcon name="text" size={17} />,
      // Live document only — nothing for Cancel to restore.
      fields: [],
      presenceKeys: ['injuries.description-of-injuries'],
      hint: 'A live document · saves as you type · not part of Cancel',
      headerExtra: <span className={`${CHIP_N} mr-1`}><KIcon name="sync" size={12} />Live document</span>,
      // LIVE read-only view (saved copy until synced / if collab is down).
      view: (ctx) => (
        <Clamp maxH={150} moreLabel="Open to edit" onMore={ctx.onAdd} showMore={htmlHasText(descHtml)}>
          <LiveDocView docName={descDoc} html={descHtml} onSnapshot={descSnapshot} collabUser={collabUser} empty={<Empty onAdd={ctx.onAdd} />} />
        </Clamp>
      ),
      edit: () => (
        // Description of Injuries — Yjs collaborative rich-text. The HTML
        // mirror is clientDetails.descriptionOfInjuries (descSnapshot).
        <FormField variant="info" label="Description of injuries" fieldKey="injuries.description-of-injuries">
          <LiveDocEdit>
            {(onStatus) => (
              <RichTextEditor
                collabDocName={descDoc}
                collabToken={token}
                initialHtml={DOMPurify.sanitize(descHtml)}
                onSnapshot={descSnapshot}
                placeholder="Describe the injuries…"
                collabUser={collabUser}
                onCollabStatus={onStatus}
              />
            )}
          </LiveDocEdit>
        </FormField>
      ),
    },
  ]

  const words = wordCount(descHtml)
  const glance = (
    <Glance cells={[
      {
        icon: 'heart', label: 'Areas',
        value: affected.length ? <span className="tabular-nums">{affected.length} of {INJURY_FIELDS.length}</span> : <GlanceDash />,
        sub: affected.length
          ? `${affected.slice(0, 3).map((x) => x.label).join(', ')}${affected.length > 3 ? ` +${affected.length - 3}` : ''}`
          : 'At least one is required',
      },
      { icon: 'brain', label: 'Psychological', value: inj.psychological ? 'Yes' : 'No', sub: inj.psychological ? 'Psychological injury recorded' : 'Not recorded as affected' },
      { icon: 'alert', label: 'Severity', value: inj.exceptionallySevere ? 'Exceptionally severe' : 'Standard', sub: inj.exceptionallySevere ? 'Details are a live document' : 'Not exceptionally severe' },
      { icon: 'text', label: 'Description', value: words ? 'Recorded' : <GlanceDash />, sub: words ? `${words} word${words === 1 ? '' : 's'}` : 'Description of injuries' },
    ]} />
  )

  return (
    <SectionCards<InjuryDetails>
      value={injuries}
      onChange={updateMany}
      fieldKeyOf={FIELD_KEY}
      onBroadcast={broadcastFieldEdits}
      onFlush={flushAutoSave}
      errors={errors}
      defs={defs}
      title="Injury details"
      sub={showPrompt ? 'Record the injury details below' : 'Where the client was injured, and how badly.'}
      glance={glance}
      footer={<SectionFooter sectionKey="injuryDetails" errors={errors} />}
      idPrefix={`inj-${currentCase.id}`}
    />
  )
}
export default InjuryDetailsSection
